import { CreditRequestContext } from '../client/creditsClient';
import { DiscoveryRefusal } from '../types/discovery.types';
import { displayRfqId } from './rfqIdentity';

/**
 * The words and the shape of "Request more credits": what the buyer was doing
 * when they ran short, the date the pending notice shows, and Robbie's lines
 * for each outcome. Plain functions, so the hook, the controls and the design
 * gallery all say the same thing the same way.
 */

export const REQUEST_CREDITS_LABEL = 'Request more credits';

/** Said once the request is in: the consequence, then what happens next. */
export const REQUESTED_MESSAGE =
  "I've asked our team for more credits. You'll get an email when they're added.";

/** `3 Oct` — the day the request was made, as the pending notice reads it. */
export const requestedOnLabel = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? 'an earlier day'
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

export const pendingNotice = (requestedOn: string): string =>
  `Requested on ${requestedOn}, waiting for our team`;

/** `4 Oct, 14:30` — the moment the wait after a decline ends, in the reader's own clock. */
export const askAgainLabel = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? 'later'
    : date.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
};

/** Said instead of the button while the wait after a decline runs. */
export const declinedNotice = (declinedOn: string, askAgainAt: string): string =>
  `Our team declined this on ${declinedOn}. You can ask again after ${askAgainAt}.`;

/** Robbie's line when the request could not be sent. `reason` is the server's own sentence, when it gave one. */
export const requestFailedMessage = (reason?: string): string => {
  const why = reason ? ` — ${reason.replace(/\.\s*$/, '')}` : '';
  return `I couldn't send your request${why}. Nothing changed, so ask me again in a moment.`;
};

/**
 * The shortfall a refusal or gate describes, as the request carries it. The
 * server's balance can be unknown (UMS did not say), and a shortfall without
 * the balance is not one — so then only the RFQ goes along. `rfqLabel` is the
 * display id; the stored id's org code is plumbing and never leaves the app.
 */
export const creditRequestContext = (refusal: DiscoveryRefusal, rfqLabel?: string): CreditRequestContext => {
  const rfqId = rfqLabel ? displayRfqId(rfqLabel) : '';
  return {
    ...(rfqId ? { rfqId } : {}),
    ...(refusal.balance === null
      ? {}
      : { shortfall: { components: refusal.components, cost: refusal.cost, balance: refusal.balance } }),
  };
};
